import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  AppState,
  Image,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View
} from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { StatusBar } from 'expo-status-bar'
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router'
import { useQuery, useMutation, useConvex } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { DS } from '@/design-system'
import { useStore } from '@/models'
import { hasAdminRights } from '@ppwr/convex/lounges/shared'
import { events } from '@/models/analytics/analytics-events'
import { translate } from '../../i18n'
import { LoungeAvatar } from '../../components/LoungeAvatar'
import { useConfirmDialog } from '../../components/ConfirmDialog'
import { LoungePlayTab } from './components/LoungePlayTab'
import { LoungeMembersTab } from './components/LoungeMembersTab'
import { useSaveLoungeJoinCode } from './components/useSaveLoungeJoinCode'
import { LoungeChatTab } from './components/LoungeChatTab'
import { ToggleTabs } from '../../components/ToggleTabs'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const backIcon = require('../../../assets/mainHeader/back.png')

type Tab = 'play' | 'chat' | 'members'

function buildTabOptions(): { value: Tab; label: string }[] {
  return [
    { value: 'play', label: translate('loungeLobbyScreen.play') },
    { value: 'chat', label: translate('loungeLobbyScreen.chat') },
    { value: 'members', label: translate('loungeLobbyScreen.members') }
  ]
}

export function LoungeLobbyScreen() {
  const { loungeId: loungeIdStr } = useLocalSearchParams<{ loungeId: string }>()
  const loungeId = loungeIdStr as Id<'lounges'> | undefined
  const router = useRouter()
  const convex = useConvex()
  const {
    analytics: { setEvent },
    session: { user, isTeacher, isAdmin },
    game: { setPlayKind }
  } = useStore()
  const userId = user?.auth0Id ?? ''
  const mongoUserId = user?._id?.toString() ?? ''
  const userName = user?.name ?? 'Player'

  const lobby = useQuery(api.lounges.queries.getLoungeLobby, loungeId ? { loungeId, userId } : 'skip')
  const messages = useQuery(api.lounges.queries.getLoungeMessages, loungeId ? { loungeId } : 'skip')
  const games = useQuery(api.lounges.queries.getLoungeGames, loungeId ? { loungeId } : 'skip')
  const scheduledGames = useQuery(api.lounges.queries.getLoungeScheduledGames, loungeId ? { loungeId } : 'skip')

  const ping = useMutation(api.lounges.mutations.heartbeatLoungePresence)
  const leavePing = useMutation(api.lounges.mutations.leaveLoungePresence)
  const sendMessage = useMutation(api.lounges.mutations.sendLoungeMessage)
  const rotateCode = useMutation(api.lounges.mutations.rotateLoungeJoinCode)

  const [activeTab, setActiveTab] = useState<Tab>('play')
  const [rotating, setRotating] = useState(false)

  const { showConfirm, dialogPortal } = useConfirmDialog()

  // Presence ping — only while the lobby is the FOCUSED screen. The lounge stack uses
  // freezeOnBlur, so this screen stays mounted (frozen) behind a pushed game. Gating on
  // focus stops it from pinging isInGame:false over the game screen's isInGame:true.
  useFocusEffect(
    useCallback(() => {
      if (!loungeId || !userId) return
      const doPing = () => ping({ loungeId: loungeId as Id<'lounges'>, userId, userName, isInGame: false })
      doPing()
      const id = setInterval(doPing, 120_000)
      // Cleanup runs on blur (e.g. pushing a game) or unmount — just stop pinging.
      // We intentionally do NOT leave here, so navigating into a game keeps the row.
      return () => clearInterval(id)
    }, [loungeId, userId, userName, ping])
  )

  // Remove presence when leaving the lobby entirely. With freezeOnBlur the lobby only
  // unmounts when it's popped off the stack (back to the lounge list), not when a game is
  // pushed on top — so this fires exactly when the user truly leaves the lobby.
  useEffect(() => {
    if (!loungeId || !userId) return
    return () => {
      leavePing({ loungeId: loungeId as Id<'lounges'>, userId }).catch(() => {})
    }
  }, [loungeId, userId, leavePing])

  // App background/foreground — remove presence on background, re-ping on foreground
  useEffect(() => {
    if (!loungeId || !userId) return
    const subscription = AppState.addEventListener('change', (nextAppState) => {
      if (nextAppState === 'background' || nextAppState === 'inactive') {
        leavePing({ loungeId: loungeId as Id<'lounges'>, userId }).catch(() => {})
      } else if (nextAppState === 'active') {
        ping({ loungeId: loungeId as Id<'lounges'>, userId, userName, isInGame: false })
      }
    })
    return () => subscription.remove()
  }, [loungeId, userId, userName, ping, leavePing])

  const handleJoinGame = useCallback(
    async (gameId: string) => {
      // Teachers join as observers server-side and pick their seat in-game (TeacherSeatChoiceModal).
      try {
        await convex.mutation(api.games.mutations.joinGame, {
          gameId: gameId as Id<'games'>,
          userId,
          mongoUserId,
          name: userName
        })
      } catch (err: any) {
        const msg: string = err?.message ?? ''
        Alert.alert(
          translate('loungeLobbyScreen.unableToJoin'),
          msg.includes('full')
            ? translate('loungeLobbyScreen.joinFull')
            : translate('loungeLobbyScreen.joinUnavailable')
        )
        return
      }
      router.push({ pathname: '/game/[gameId]', params: { gameId } } as any)
    },
    [convex, userId, mongoUserId, userName, router]
  )

  const handleViewGame = useCallback(
    (gameId: string) => {
      router.push({ pathname: '/game/[gameId]', params: { gameId } } as any)
    },
    [router]
  )

  const handleViewSchedule = useCallback(
    (hash: string) => {
      router.push(`/game/join/accept-scheduled-invite/${hash}` as any)
    },
    [router]
  )

  const handleCreateGame = useCallback(() => {
    if (!loungeId) return
    setPlayKind('player')
    router.push(`/game/new?loungeId=${loungeId}`)
  }, [loungeId, router, setPlayKind])

  const handleRotateCode = useCallback(() => {
    if (!loungeId) return
    showConfirm({
      title: translate('loungeSettingsScreen.rotateAlert.title'),
      message: translate('loungeSettingsScreen.rotateAlert.message'),
      cancelText: translate('loungeSettingsScreen.rotateAlert.cancel'),
      confirmText: translate('loungeSettingsScreen.rotateAlert.confirm'),
      onConfirm: async () => {
        setRotating(true)
        try {
          await rotateCode({ loungeId: loungeId as Id<'lounges'>, actorId: userId })
        } finally {
          setRotating(false)
        }
      }
    })
  }, [loungeId, userId, rotateCode, showConfirm])

  const handleSaveCode = useSaveLoungeJoinCode(loungeId as Id<'lounges'> | undefined, userId)

  const handleTabPress = useCallback(
    (tab: Tab) => {
      setActiveTab(tab)
      const event = tab === 'chat' ? events.lounge.lounge_chat_tapped : events.lounge.lounge_tab_pressed
      setEvent(event, {
        userId: user?.auth0Id ?? '',
        loungeId,
        tag: tab,
        isTeacher,
        isAdmin
      })
    },
    [loungeId, user?.auth0Id, isTeacher, isAdmin, setEvent]
  )

  const handleOpenSettings = useCallback(() => {
    if (!loungeId) return
    if (lobby?.callerRole && hasAdminRights(lobby.callerRole)) {
      router.push({ pathname: '/lounges/[loungeId]/settings', params: { loungeId } } as any)
    } else {
      router.push({ pathname: '/lounges/[loungeId]/settings/configuration', params: { loungeId } } as any)
    }
  }, [lobby?.callerRole, loungeId, router])

  const handleBackPress = useCallback(() => {
    if (router.canGoBack()) router.back()
    else router.push('/lounges' as any)
  }, [router])

  if (lobby === null) {
    router.replace('/lounges')
    return null
  }

  if (!lobby) {
    return (
      <SafeAreaView style={styles.container}>
        {dialogPortal}
        <StatusBar style="dark" />
        <View style={styles.headerRow}>
          <TouchableOpacity onPress={handleBackPress} style={styles.headerIconButton}>
            <Image source={backIcon} style={styles.headerBackIcon} resizeMode="contain" />
          </TouchableOpacity>
        </View>
        <View style={styles.centered}>
          <DS.Text variant="Body Large/Regular">Loading lounge...</DS.Text>
        </View>
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView style={styles.container} edges={['top', 'left', 'right']}>
      {dialogPortal}
      <StatusBar style="dark" />
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={handleBackPress} style={styles.headerIconButton} accessibilityLabel="Back">
            <Image source={backIcon} style={styles.headerBackIcon} resizeMode="contain" />
          </TouchableOpacity>
          <View style={styles.headerTitle}>
            <LoungeAvatar name={lobby.name} branding={lobby.branding} size={24} />
            <DS.Text variant="Heading 3/Medium" style={styles.headerTitleText} numberOfLines={1} adjustsFontSizeToFit>
              {lobby.name}
            </DS.Text>
          </View>
          <View style={styles.headerActions}>
            <TouchableOpacity
              onPress={handleOpenSettings}
              style={styles.headerIconButton}
              accessibilityLabel="Lounge settings"
            >
              <DS.Icon name="Switches" size={24} color="Global/Black" />
            </TouchableOpacity>
          </View>
        </View>

        {/* Tabs */}
        <View style={styles.tabBarWrapper}>
          <ToggleTabs<Tab> options={buildTabOptions()} value={activeTab} onChange={handleTabPress} />
        </View>

        {/* Tab Content */}
        {activeTab === 'play' && (
          <LoungePlayTab
            games={games ?? []}
            scheduledGames={scheduledGames ?? []}
            joinCode={lobby.joinCode ?? null}
            callerRole={lobby.callerRole}
            onJoin={handleJoinGame}
            onView={handleViewGame}
            onViewSchedule={handleViewSchedule}
            onCreateGame={handleCreateGame}
            onRotateCode={handleRotateCode}
            rotating={rotating}
            loungeId={loungeId as Id<'lounges'> | undefined}
            onSaveCode={handleSaveCode}
          />
        )}

        {activeTab === 'chat' && loungeId && (
          <LoungeChatTab
            messages={messages}
            userId={userId}
            loungeId={loungeId}
            userName={userName}
            chatEnabled={lobby.settings.chatEnabled}
            sendMessage={sendMessage}
            onJoinGame={handleJoinGame}
            onMessageSent={(length) =>
              setEvent(events.lounge.lounge_chat_sent, {
                userId,
                loungeId,
                messageLength: length,
                isTeacher,
                isAdmin
              })
            }
          />
        )}

        {activeTab === 'members' && loungeId && (
          <LoungeMembersTab
            loungeId={loungeId}
            members={lobby.members}
            currentUserId={userId}
            currentUserName={user?.name}
            callerRole={lobby.callerRole}
            joinCode={lobby.joinCode ?? null}
            onRotateCode={handleRotateCode}
            rotating={rotating}
            onSaveCode={handleSaveCode}
          />
        )}
      </KeyboardAvoidingView>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    height: 54
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    height: 54
  },
  headerIconButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center'
  },
  headerBackIcon: {
    width: 24,
    height: 24
  },
  headerTitle: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 8
  },
  headerTitleText: {
    color: DS.palette['Brand/Maroon'],
    flexShrink: 1,
    fontSize: 24
  },
  tabBarWrapper: {
    paddingVertical: 16
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4
  }
})
